import React, { useState } from "react";
import { CATEGORY_LABELS, CATEGORY_DESC, REALM_GROUPS } from "../lib/api";
import { GROUP_COLORS, RealmIcon } from "../lib/realmMeta";

// Two-level realm browser: group tabs → realm chips within the chosen group.
// onChange receives { params, realm, group } where params is passed straight to the API
// ({} = all, { categories } = a whole group, { category } = a single realm).
export const RealmFilter = ({ accent = "#A855F7", onChange, showDesc = true, testIdPrefix = "filter" }) => {
  const [group, setGroup] = useState("");
  const [realm, setRealm] = useState("");

  const emit = (g, r) => {
    if (r) return onChange({ params: { category: r }, realm: r, group: g });
    if (g) {
      const grp = REALM_GROUPS.find((x) => x.key === g);
      return onChange({ params: { categories: grp.domains.join(",") }, realm: "", group: g });
    }
    onChange({ params: {}, realm: "", group: "" });
  };

  const pickGroup = (g) => { const ng = g === group ? "" : g; setGroup(ng); setRealm(""); emit(ng, ""); };
  const pickRealm = (r) => { const nr = r === realm ? "" : r; setRealm(nr); emit(group, nr); };

  const activeGroup = REALM_GROUPS.find((x) => x.key === group);
  const groupColor = activeGroup ? (GROUP_COLORS[activeGroup.key] || accent) : accent;
  const chip = "shrink-0 inline-flex items-center gap-1.5 px-4 py-2 font-body text-xs tracking-wider uppercase border transition-colors";
  const onStyle = (color) => ({ borderColor: color, color });
  const cls = (isOn) => `${chip} ${isOn ? "" : "border-white/15 text-zinc-400 hover:text-white"}`;

  return (
    <div className="mb-8" data-testid="realm-filter">
      <div className="flex gap-2 overflow-x-auto pb-3 -mx-4 px-4 md:mx-0 md:px-0" data-testid={`${testIdPrefix}-groups`}>
        <button onClick={() => pickGroup("")} data-testid={`${testIdPrefix}-all`} style={!group ? onStyle(accent) : {}} className={cls(!group)}>Scroll All</button>
        {REALM_GROUPS.map((g) => {
          const gc = GROUP_COLORS[g.key] || accent;
          const on = group === g.key;
          return (
            <button key={g.key} onClick={() => pickGroup(g.key)} data-testid={`${testIdPrefix}-group-${g.key}`} style={on ? onStyle(gc) : {}} className={cls(on)}>
              <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: gc }} />
              {g.label}
            </button>
          );
        })}
      </div>

      {activeGroup && (
        <div className="flex gap-2 overflow-x-auto pb-3 -mx-4 px-4 md:mx-0 md:px-0 mt-1" data-testid={`${testIdPrefix}-realms`}>
          <button onClick={() => pickRealm("")} data-testid={`${testIdPrefix}-realm-all`} style={!realm ? onStyle(groupColor) : {}} className={`${cls(!realm)} !text-[11px] !py-1.5`}>All {activeGroup.label}</button>
          {activeGroup.domains.map((d) => {
            const on = realm === d;
            return (
              <button key={d} onClick={() => pickRealm(d)} data-testid={`${testIdPrefix}-${d}`} style={on ? onStyle(groupColor) : {}} className={`${cls(on)} !text-[11px] !py-1.5`}>
                <RealmIcon realm={d} size={13} style={{ color: on ? groupColor : groupColor }} className="shrink-0 opacity-90" />
                {CATEGORY_LABELS[d]}
              </button>
            );
          })}
        </div>
      )}

      {showDesc && realm && CATEGORY_DESC[realm] && (
        <p className="font-body text-sm text-zinc-400 leading-snug mt-3" data-testid="domain-desc">
          <span className="text-white">{CATEGORY_LABELS[realm]}</span> · {CATEGORY_DESC[realm]}
        </p>
      )}
    </div>
  );
};
